import { type FnbNocOverview, type MeResponse, TAKEAWAY_ORDER_STATUSES, type TakeawayOrderNocItem, type TakeawayOrderStatus } from "@seagames/contracts";
import { Icons, KeysetPager, ListHeaderButton, ListToolbar, type StatsCell, StatsStrip, useUiStrings } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fnbNocOptions, takeawayOrdersOptions } from "../../lib/api/fnb";
import { formatDayShort, zoneAbbreviation } from "../../lib/format/datetime";
import { type SearchNavigate, useListPage } from "../../lib/list-page/use-list-page";
import { EmptyCard } from "../common/empty-card";
import { StatusGuide } from "../common/status-guide";
import { MealIcon } from "../icons/sidebar-icons";
import { BedIcon } from "../accommodation/icons";
import { CancelOrderModal } from "./order-status-modal";
import { OrderFormModal } from "./order-form-modal";
import { DietPacks, LoadFailed, OrderStatusChip, OrderStatusDot, Timetable, joinList, useDietLabel, useOrderStatusGuide } from "./parts";

const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined);
const PAGE_SIZES = [25, 50, 100] as const;

/** The NOC page's URL keys: its status chip and the keyset. */
export const nocFnbSearchShape = {
	status: lenient(z.enum(TAKEAWAY_ORDER_STATUSES)),
	limit: lenient(z.coerce.number().int().min(1).max(100)),
	cursor: lenient(z.string().min(1).max(200)),
	prev: lenient(z.array(z.string().min(1).max(200)).max(100)),
};
export type NocFnbSearch = z.infer<z.ZodObject<typeof nocFnbSearchShape>>;

/**
 * A contingent's Food & Beverages page (her NOC `#/nml`, PLAN F10):
 *   strip    Orders · Waiting for the host · Confirmed · Rejected — its own
 *   section  Where your delegation eats: the halls serving the buildings its
 *            own people sleep in, with address, timetable (games' clock) and
 *            diets; only its own buildings and people counts
 *   section  Packed meals: its own orders (newest first, keyset), a status
 *            chip, Order packed meals, Cancel with a reason
 * No meal plan, nothing of another organisation: both reads are the NOC's
 * own shapes (`GET /fnb/noc`, `GET /takeaway-orders` with `view: "noc"`).
 */
export function NocFnbView({
	session,
	gamesId,
	timeZone,
	search,
	navigate,
}: {
	session: Pick<MeResponse, "active_role" | "group">;
	gamesId: string;
	timeZone: string;
	search: NocFnbSearch;
	navigate: SearchNavigate<NocFnbSearch>;
}) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const overview = useQuery(fnbNocOptions(gamesId));
	const o = overview.data;
	const cutoff = o?.takeaway_cutoff_hours ?? 24;
	const guide = useOrderStatusGuide(cutoff);
	const [ordering, setOrdering] = useState(false);
	const [cancelling, setCancelling] = useState<TakeawayOrderNocItem | null>(null);
	const ownHallIds = useMemo(() => new Set((o?.halls ?? []).map((h) => h.dining_hall_id)), [o]);
	const zone = zoneAbbreviation(timeZone);

	const page = useListPage({
		paging: "keyset",
		search,
		navigate,
		listOptions: (q) => takeawayOrdersOptions({ games_id: gamesId, status: search.status, sort: "recent", cursor: q.cursor, limit: q.limit }),
		columnsKey: "fnb-noc-orders",
		pageSizes: PAGE_SIZES,
		defaultPageSize: 25,
		summary: {
			showing: (v) => t("orders.pager.showing", v),
			showingNoTotal: (v) => t("orders.pager.showingNoTotal", v),
			empty: t("orders.pager.empty"),
		},
	});
	const rows = page.rows;

	const count = (s: TakeawayOrderStatus) => o?.order_counts.find((c) => c.status === s)?.count ?? 0;
	const all = o ? o.order_counts.reduce((n, c) => n + c.count, 0) : 0;
	const cells: StatsCell[] = [
		{ key: "orders", label: t("noc.strip.orders"), value: o ? all : "—" },
		{ key: "waiting", label: t("noc.strip.waiting"), value: o ? count("PENDING") : "—" },
		{ key: "confirmed", label: t("noc.strip.confirmed"), value: o ? count("CONFIRMED") : "—" },
		{ key: "rejected", label: t("noc.strip.rejected"), value: o ? count("REJECTED") : "—", note: o ? (count("REJECTED") > 0 ? t("noc.strip.seeReason") : t("noc.strip.none")) : undefined },
	];

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<StatsStrip label={t("noc.strip.aria")} cells={cells} className={overview.isFetching && o ? "opacity-60 transition-opacity" : "transition-opacity"} />

			<section aria-labelledby="fnb-noc-halls-h" className="grid gap-[0.5rem]">
				<div className="mx-[0.25rem] flex flex-wrap items-baseline justify-between gap-[0.5rem]">
					<h2 id="fnb-noc-halls-h" className="text-[0.875rem] font-bold text-gms-navy-ink">
						{t("noc.eats")}
					</h2>
					<p className="text-[0.75rem] text-gms-ink-3">{t("time.zoneNote", { zone })}</p>
				</div>
				{overview.isError ? (
					<div className="rounded-[0.625rem] border border-gms-line bg-base-100">
						<LoadFailed error={overview.error} onRetry={() => void overview.refetch()} />
					</div>
				) : !o ? (
					<div className="grid gap-[0.625rem] sm:grid-cols-2 xl:grid-cols-3" aria-busy>
						<div className="skeleton h-[9rem] rounded-[0.625rem]" />
						<div className="skeleton h-[9rem] rounded-[0.625rem]" />
					</div>
				) : o.halls.length === 0 ? (
					<EmptyCard icon={<MealIcon />} title={t("noc.noHall")}>
						{t("noc.noHallHint")}
					</EmptyCard>
				) : (
					<ul className="grid gap-[0.625rem] sm:grid-cols-2 xl:grid-cols-3">
						{o.halls.map((h) => (
							<li key={h.dining_hall_id} className="min-w-0">
								<NocHallCard hall={h} />
							</li>
						))}
					</ul>
				)}
			</section>

			<section aria-labelledby="fnb-noc-orders-h" className="grid gap-[0.75rem]">
				<header className="flex flex-wrap items-center justify-between gap-[0.625rem]">
					<h2 id="fnb-noc-orders-h" className="mx-[0.25rem] flex items-baseline gap-[0.5rem] text-[0.875rem] font-bold text-gms-navy-ink">
						{t("noc.packed")}
						{page.total !== null && <span className="text-[0.78125rem] font-normal text-gms-ink-3 tabular-nums">{t("orders.count", { count: page.total, n: fmt(page.total) })}</span>}
					</h2>
					<ListHeaderButton tone="primary" icon={<Icons.Plus />} onClick={() => setOrdering(true)} disabled={!o}>
						{t("noc.order")}
					</ListHeaderButton>
				</header>
				<ListToolbar
					chips={[
						{
							kind: "options",
							key: "status",
							label: t("orders.chips.status"),
							options: TAKEAWAY_ORDER_STATUSES.map((s) => ({ value: s, label: t(`status.label.${s}`), icon: <OrderStatusDot status={s} />, count: o ? count(s) : undefined })),
							value: search.status ? [search.status] : [],
							onChange: (v) => page.patch({ status: (v[0] as TakeawayOrderStatus | undefined) ?? undefined }),
						},
					]}
					end={<StatusGuide title={guide.title} groups={guide.groups} size="sm" />}
					onClearAll={() => page.patch({ status: undefined })}
				/>
				{page.list.isError ? (
					<EmptyCard
						tone="bad"
						title={t("common.loadFailed")}
						actions={
							<button type="button" className="btn btn-sm btn-outline" onClick={() => void page.list.refetch()}>
								{t("common.tryAgain")}
							</button>
						}
					>
						{page.table.error?.message}
					</EmptyCard>
				) : page.list.isPending ? (
					<div className="grid gap-[0.625rem]" aria-busy>
						<div className="skeleton h-[5rem] rounded-[0.625rem]" />
						<div className="skeleton h-[5rem] rounded-[0.625rem]" />
					</div>
				) : rows.length === 0 ? (
					<EmptyCard
						icon={<MealIcon />}
						title={search.status ? t("orders.empty.filteredTitle") : t("noc.empty")}
						actions={
							search.status ? (
								<button type="button" className="btn btn-sm btn-outline" onClick={() => page.patch({ status: undefined })}>
									{t("orders.empty.clear")}
								</button>
							) : undefined
						}
					>
						{search.status ? t("orders.empty.filteredHint") : t("noc.emptyHint")}
					</EmptyCard>
				) : (
					<>
						<ul className={`grid gap-[0.625rem] ${page.list.isFetching ? "opacity-70 transition-opacity" : "transition-opacity"}`}>
							{rows.map((r) => (
								<li key={r.takeaway_order_id}>
									<NocOrderCard order={r} onCancel={() => setCancelling(r)} />
								</li>
							))}
						</ul>
						<KeysetPager {...page.pager} position="bottom" />
					</>
				)}
			</section>

			{ordering && <OrderFormModal session={session} gamesId={gamesId} timeZone={timeZone} cutoffHours={cutoff} noc ownHallIds={ownHallIds} onClose={() => setOrdering(false)} />}
			{cancelling && <CancelOrderModal order={cancelling} onClose={() => setCancelling(null)} />}
		</div>
	);
}

/** Her NOC hall card: name, address, the timetable, "Bring your AC card. All meals are halal; also …", and where its own people sleep. */
function NocHallCard({ hall: h }: { hall: FnbNocOverview["halls"][number] }) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const dietLabel = useDietLabel();
	return (
		<article className="grid h-full gap-[0.625rem] rounded-[0.625rem] border border-gms-line bg-base-100 p-[0.875rem]">
			<span className="flex items-start gap-[0.625rem]">
				<span className="grid size-[2.25rem] shrink-0 place-items-center rounded-[0.5rem] bg-gms-navy-soft text-gms-navy [&>svg]:size-[1.125rem]">
					<MealIcon />
				</span>
				<span className="grid min-w-0 flex-1 gap-[0.125rem]">
					<b className="text-[0.9375rem] font-semibold text-gms-navy-ink">{h.name}</b>
					<span className="text-[0.75rem] text-gms-ink-3">{h.address ?? h.cluster.name}</span>
					{h.phone && <span className="text-[0.75rem] text-gms-ink-3 tabular-nums">{h.phone}</span>}
				</span>
			</span>
			<Timetable sessions={h.sessions} />
			<span className="text-[0.78125rem] text-gms-ink-2">{h.diets.length > 0 ? t("noc.bringCardDiets", { diets: joinList(h.diets.map(dietLabel), { lower: true }) }) : t("noc.bringCard")}</span>
			<span className="flex items-start gap-[0.375rem] text-[0.75rem] text-gms-ink-3">
				<BedIcon aria-hidden className="mt-[0.125rem] size-[0.875rem] shrink-0" />
				{joinList(h.buildings.map((b) => t("noc.buildingPeople", { building: b.name, count: b.people, n: fmt(b.people) })))}
			</span>
		</article>
	);
}

/** One order (her `.l3ord`): "N packs · Lunch, Sun 19 Sep", ref · pick-up at hall · ordered late, diets, the host's note, status, Cancel. */
function NocOrderCard({ order: r, onCancel }: { order: TakeawayOrderNocItem; onCancel: () => void }) {
	const { t } = useTranslation("fnb");
	const { formatNumber: fmt } = useUiStrings();
	const closed = r.status === "REJECTED" || r.status === "CANCELLED" || r.status === "COLLECTED";
	return (
		<article className={`flex flex-wrap items-start gap-x-[0.875rem] gap-y-[0.5rem] rounded-[0.625rem] border border-gms-line px-[1rem] py-[0.75rem] ${closed ? "bg-gms-soft/60" : "bg-base-100"}`}>
			<span className="grid size-[2.25rem] shrink-0 place-items-center rounded-[0.5rem] bg-gms-navy-soft text-gms-navy [&>svg]:size-[1.125rem]">
				<MealIcon />
			</span>
			<div className="grid min-w-0 flex-[1_1_16rem] gap-[0.1875rem]">
				<b className="text-[0.90625rem] font-semibold text-gms-navy-ink">
					{t("noc.orderTitle", { count: r.packs, n: fmt(r.packs), meal: t(`meal.${r.meal}`), day: formatDayShort(r.service_date) })}
				</b>
				<span className="text-[0.78125rem] text-gms-ink-3">
					{t("noc.orderLine", { ref: r.ref, time: r.pickup_time, hall: r.dining_hall.name })}
					{r.late && (
						<>
							{" · "}
							<span className="font-semibold text-gms-warn">{t("noc.orderedLate")}</span>
						</>
					)}
				</span>
				<span className="text-[0.78125rem] text-gms-ink-2">
					<DietPacks diets={r.diets} noneKey="noc.standardPacks" />
				</span>
				{r.status_note && r.status !== "PENDING" && (
					<span className="rounded-[0.375rem] bg-gms-soft-2 px-[0.5rem] py-[0.25rem] text-[0.78125rem] text-gms-ink-2">{t("noc.hostNote", { note: r.status_note })}</span>
				)}
			</div>
			<div className="ml-auto flex items-center gap-[0.5rem]">
				<OrderStatusChip status={r.status} />
				{r.next_statuses.includes("CANCELLED") && (
					<button type="button" className="btn btn-outline btn-sm text-gms-bad" onClick={onCancel}>
						{t("noc.cancel")}
					</button>
				)}
			</div>
		</article>
	);
}
